import { useEffect, useRef, useState } from "react";
import {
  AppBar,
  ClickAwayListener,
  Container,
  Grid,
  Grow,
  MenuList,
  Paper,
  Popper,
  Toolbar,
  Box,
  IconButton,
  InputBase,
  Fade,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useParams, useLocation } from "react-router";

import { ReactComponent as ArrowDown } from "assets/icons/chevron-down.svg";
import Logo from "assets/images/logo-prefeitura.png";
import {
  AppBarWrapper,
  LogoImage,
  MenuButonGridItem,
  MenuButton,
  MenuListOption,
  MenuListWrapper,
  SubMenuItem,
  EntityInfoWrapper,
  EntityTypeText,
  EntityCityName,
  LogoEntityWrapper,
} from "./navBar.styles";
import { ReactComponent as Info } from "assets/icons/info.svg";
import { ReactComponent as Menu } from "assets/icons/menu.svg";
import { FlyoutMenuItem } from "./flyoutMenuItem";
import DrawerComponent from "../drawer";
import { useGlobalState } from "../../../global/context/global-context";
import { formatEntityName } from "../../../../utils/entity-formatter";
import { MegaMenuComponent } from "./megaMenu";

function useOutSiteClickAlert(ref: any, action: any) {
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        action();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });
}

export function NavBarComponent() {
  const anchorMunicipioRef = useRef<HTMLButtonElement>(null);
  const anchorDiarioRef = useRef<HTMLButtonElement>(null);
  const anchorServicosRef = useRef<HTMLButtonElement>(null);
  const anchorTransparenciaRef = useRef<HTMLButtonElement>(null);
  const anchorOutrasOpcoesRef = useRef<HTMLButtonElement>(null);
  const subMenuRef = useRef(null);
  const navigate = useNavigate();
  const routeParams = useParams<{ state?: string; city?: string }>();
  const location = useLocation();

  const buildPath = (path: string): string => {
    if (!routeParams.state || !routeParams.city) return path;
    if (path === '/') return `/${routeParams.state}/${routeParams.city}`;
    return `/${routeParams.state}/${routeParams.city}${path}`;
  };

  const [openMunicipio, setOpenMunicipio] = useState(false);
  const [openDiario, setOpenDiario] = useState(false);
  const [openServicos, setOpenServicos] = useState(false);
  const [openTransparencia, setOpenTransparencia] = useState(false);
  const [openOutrasOpcoes, setOpenOutrasOpcoes] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { applicationData } = useGlobalState();

  const menus = applicationData?.menus || [];
  const hasMenus = menus.length > 0;

  useOutSiteClickAlert(subMenuRef, () => { });

  // Close MegaMenu when ESC key is pressed
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && openOutrasOpcoes) {
        setOpenOutrasOpcoes(false);
      }
    };

    if (openOutrasOpcoes) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [openOutrasOpcoes]);

  const handleSearchToggle = () => {
    setSearchOpen(!searchOpen);
    if (searchOpen) {
      setSearchTerm('');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim().length >= 3) {
      navigate(`${buildPath('/busca')}?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchOpen(false);
      setSearchTerm('');
    }
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenMunicipio(false);
      setOpenDiario(false);
      setOpenServicos(false);
      setOpenTransparencia(false);
      setOpenOutrasOpcoes(false);
    }
  }

  const handleCloseMunicipio = (event: MouseEvent | TouchEvent | React.MouseEvent) => {
    if (
      anchorMunicipioRef.current &&
      anchorMunicipioRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenMunicipio(false);
  };

  const handleCloseDiario = (event: MouseEvent | TouchEvent | React.MouseEvent) => {
    if (
      anchorDiarioRef.current &&
      anchorDiarioRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenDiario(false);
  };

  const handleCloseServicos = (event: MouseEvent | TouchEvent | React.MouseEvent) => {
    if (
      anchorServicosRef.current &&
      anchorServicosRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenServicos(false);
  };

  const handleCloseTransparencia = (event: MouseEvent | TouchEvent | React.MouseEvent) => {
    if (
      anchorTransparenciaRef.current &&
      anchorTransparenciaRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenTransparencia(false);
  };

  const handleToggleMunicipio = () => {
    setOpenMunicipio((prevOpen) => !prevOpen);
  };

  const handleToggleDiario = () => {
    setOpenDiario((prevOpen) => !prevOpen);
  };

  const handleToggleServicos = () => {
    setOpenServicos((prevOpen) => !prevOpen);
  };

  const handleToggleTransparencia = () => {
    setOpenTransparencia((prevOpen) => !prevOpen);
  };

  const handleCloseOutrasOpcoes = (event: MouseEvent | TouchEvent | React.MouseEvent) => {
    if (
      anchorOutrasOpcoesRef.current &&
      anchorOutrasOpcoesRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenOutrasOpcoes(false);
  };

  const handleToggleOutrasOpcoes = () => {
    setOpenOutrasOpcoes((prevOpen) => !prevOpen);
  };

  const checkLocation = (route: string) => {
    return location.pathname === buildPath(route);
  };

  const entityDisplay = formatEntityName(applicationData?.entity?.name);

  return (
    <>
      <AppBarWrapper>
        <AppBar position="static" color="transparent">
          <Container maxWidth="xl" disableGutters>
            <Toolbar disableGutters sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 0, padding: 0 }}>
              <Grid
                container
                spacing={2}
                justifyContent="space-between"
                alignItems="center"
                sx={{ padding: '8px 0' }}
              >
                <Grid item xs="auto">
                  <LogoEntityWrapper onClick={() => navigate(buildPath("/"))}>
                    <LogoImage
                      src={
                        applicationData?.entity?.brandLogo
                          ? `data:image/png;base64,${applicationData?.entity?.brandLogo}`
                          : applicationData?.entity?.stateBrandLogo || Logo
                      }
                      alt=""
                    />
                    {entityDisplay.name && (
                      <EntityInfoWrapper>
                        {entityDisplay.type && <EntityTypeText>{entityDisplay.type}</EntityTypeText>}
                        <EntityCityName>{entityDisplay.name}</EntityCityName>
                      </EntityInfoWrapper>
                    )}
                  </LogoEntityWrapper>
                </Grid>

                {/* Search Bar */}
                <Grid item xs="auto" sx={{ ml: 'auto', mr: 2, display: { xs: 'none', md: 'block' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Fade in={searchOpen}>
                      <Paper
                        component="form"
                        onSubmit={handleSearch}
                        sx={{
                          display: searchOpen ? 'flex' : 'none',
                          alignItems: 'center',
                          width: { sm: 350, md: 450 },
                          px: 2,
                          py: 1,
                          borderRadius: 25,
                          boxShadow: 2,
                        }}
                      >
                        <InputBase
                          placeholder="Buscar no portal..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          sx={{ flex: 1, fontSize: '1rem' }}
                          autoFocus
                        />
                      </Paper>
                    </Fade>

                    <IconButton
                      onClick={handleSearchToggle}
                      sx={{
                        color: 'white',
                        backgroundColor: 'primary.main',
                        boxShadow: 3,
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                          boxShadow: 4,
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                      aria-label="buscar"
                      size="large"
                    >
                      {searchOpen ? <CloseIcon fontSize="large" /> : <SearchIcon fontSize="large" />}
                    </IconButton>
                  </Box>
                </Grid>

                <MenuButonGridItem className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-auto">
                  <MenuButton onClick={() => setIsDrawerOpen(true)}>
                    <Menu />
                  </MenuButton>
                </MenuButonGridItem>
              </Grid>
              <Grid container justifyContent="center">
                <Grid item xs={12}>
                  <MenuListWrapper>
                    <MenuListOption
                      active={checkLocation("/")}
                      onClick={() => navigate(buildPath("/"))}
                    >
                      INÍCIO
                    </MenuListOption>
                    <div style={{ position: "relative" }}>
                      <MenuListOption
                        active={
                          checkLocation("/a-cidade") ||
                          checkLocation("/estrutura-administrativa") ||
                          checkLocation("/projetos-governamentais")
                        }
                        ref={anchorMunicipioRef}
                        aria-controls={openMunicipio ? "menu-list-municipio" : undefined}
                        aria-haspopup="true"
                        onClick={handleToggleMunicipio}
                      >
                        O MUNICÍPIO <ArrowDown />
                      </MenuListOption>
                      <Popper
                        open={openMunicipio}
                        anchorEl={anchorMunicipioRef.current}
                        role={undefined}
                        transition
                        disablePortal
                      >
                        {({ TransitionProps, placement }) => (
                          <Grow
                            {...TransitionProps}
                            style={{
                              transformOrigin:
                                placement === "bottom"
                                  ? "center top"
                                  : "center bottom",
                            }}
                          >
                            <Paper style={{ padding: "6px 12px", width: "100%" }}>
                              <ClickAwayListener onClickAway={handleCloseMunicipio}>
                                <MenuList
                                  id="menu-list-municipio"
                                  onKeyDown={handleListKeyDown}
                                >
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/a-cidade"));
                                      handleToggleMunicipio();
                                    }}
                                  >
                                    A CIDADE
                                  </SubMenuItem>
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/estrutura-administrativa"));
                                      handleToggleMunicipio();
                                    }}
                                  >
                                    ESTRUTURA ADMINISTRATIVA
                                  </SubMenuItem>
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/projetos-governamentais"));
                                      handleToggleMunicipio();
                                    }}
                                  >
                                    PROJETOS GOVERNAMENTAIS
                                  </SubMenuItem>
                                </MenuList>
                              </ClickAwayListener>
                            </Paper>
                          </Grow>
                        )}
                      </Popper>
                    </div>
                    <MenuListOption
                      active={checkLocation("/noticias")}
                      onClick={() => navigate(buildPath("/noticias"))}
                    >
                      NOTÍCIAS
                    </MenuListOption>
                    <div style={{ position: "relative" }}>
                      <MenuListOption
                        active={
                          checkLocation("/diario-oficial") ||
                          checkLocation("/publicacoes")
                        }
                        ref={anchorDiarioRef}
                        aria-controls={openDiario ? "menu-list-diario" : undefined}
                        aria-haspopup="true"
                        onClick={handleToggleDiario}
                      >
                        DIÁRIO OFICIAL <ArrowDown />
                      </MenuListOption>
                      <Popper
                        open={openDiario}
                        anchorEl={anchorDiarioRef.current}
                        role={undefined}
                        transition
                        disablePortal
                      >
                        {({ TransitionProps, placement }) => (
                          <Grow
                            {...TransitionProps}
                            style={{
                              transformOrigin:
                                placement === "bottom"
                                  ? "center top"
                                  : "center bottom",
                            }}
                          >
                            <Paper style={{ padding: "6px 12px", width: "100%" }}>
                              <ClickAwayListener onClickAway={handleCloseDiario}>
                                <MenuList
                                  id="menu-list-diario"
                                  onKeyDown={handleListKeyDown}
                                >
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/diario-oficial"));
                                      handleToggleDiario();
                                    }}
                                  >
                                    EDIÇÕES
                                  </SubMenuItem>
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/publicacoes"));
                                      handleToggleDiario();
                                    }}
                                  >
                                    PUBLICAÇÕES
                                  </SubMenuItem>
                                </MenuList>
                              </ClickAwayListener>
                            </Paper>
                          </Grow>
                        )}
                      </Popper>
                    </div>
                    <div style={{ position: "relative" }}>
                      <MenuListOption
                        active={
                          checkLocation("/perguntas-respostas") ||
                          checkLocation("/links-uteis")
                        }
                        ref={anchorServicosRef}
                        aria-controls={openServicos ? "menu-list-servicos" : undefined}
                        aria-haspopup="true"
                        onClick={handleToggleServicos}
                      >
                        SERVIÇOS <ArrowDown />
                      </MenuListOption>
                      <Popper
                        open={openServicos}
                        anchorEl={anchorServicosRef.current}
                        role={undefined}
                        transition
                        disablePortal
                      >
                        {({ TransitionProps, placement }) => (
                          <Grow
                            {...TransitionProps}
                            style={{
                              transformOrigin:
                                placement === "bottom"
                                  ? "center top"
                                  : "center bottom",
                            }}
                          >
                            <Paper style={{ padding: "6px 12px", width: "100%" }}>
                              <ClickAwayListener onClickAway={handleCloseServicos}>
                                <MenuList
                                  id="menu-list-servicos"
                                  onKeyDown={handleListKeyDown}
                                >
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/perguntas-respostas"));
                                      handleToggleServicos();
                                    }}
                                  >
                                    PERGUNTAS FREQUENTES
                                  </SubMenuItem>
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/links-uteis"));
                                      handleToggleServicos();
                                    }}
                                  >
                                    LINKS ÚTEIS
                                  </SubMenuItem>
                                </MenuList>
                              </ClickAwayListener>
                            </Paper>
                          </Grow>
                        )}
                      </Popper>
                    </div>
                    <div style={{ position: "relative" }}>
                      <MenuListOption
                        active={
                          checkLocation("/contas-publicas") ||
                          checkLocation("/licitacoes") ||
                          checkLocation("/contratos-aquisicoes") ||
                          checkLocation("/convenios-repasses") ||
                          checkLocation("/auditoria")
                        }
                        ref={anchorTransparenciaRef}
                        aria-controls={openTransparencia ? "menu-list-transparencia" : undefined}
                        aria-haspopup="true"
                        onClick={handleToggleTransparencia}
                      >
                        <Info />
                        TRANSPARÊNCIA <ArrowDown />
                      </MenuListOption>
                      <Popper
                        open={openTransparencia}
                        anchorEl={anchorTransparenciaRef.current}
                        role={undefined}
                        transition
                        disablePortal
                      >
                        {({ TransitionProps, placement }) => (
                          <Grow
                            {...TransitionProps}
                            style={{
                              transformOrigin:
                                placement === "bottom"
                                  ? "center top"
                                  : "center bottom",
                            }}
                          >
                            <Paper style={{ padding: "6px 12px", width: "100%" }}>
                              <ClickAwayListener onClickAway={handleCloseTransparencia}>
                                <MenuList
                                  id="menu-list-transparencia"
                                  onKeyDown={handleListKeyDown}
                                >
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/contas-publicas"));
                                      handleToggleTransparencia();
                                    }}
                                  >
                                    CONTAS PÚBLICAS
                                  </SubMenuItem>
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/licitacoes"));
                                      handleToggleTransparencia();
                                    }}
                                  >
                                    LICITAÇÕES
                                  </SubMenuItem>
                                  <FlyoutMenuItem
                                    label="GESTÃO FISCAL"
                                    items={[
                                      { label: "Lei de Diretrizes Orçamentárias (LDO)", onClick: () => { navigate(buildPath("/publicacoes/4/40")); handleToggleTransparencia(); } },
                                      { label: "Plano Plurianual (PPA)", onClick: () => { navigate(buildPath("/publicacoes/4/43")); handleToggleTransparencia(); } },
                                      { label: "Lei Orçamentária Anual (LOA)", onClick: () => { navigate(buildPath("/publicacoes/4/41")); handleToggleTransparencia(); } },
                                      { label: "Relatório de Gestão Fiscal (RGF)", onClick: () => { navigate(buildPath("/publicacoes/4/45")); handleToggleTransparencia(); } },
                                      { label: "Relatório Resumido da Execução Orçamentária (RREO)", onClick: () => { navigate(buildPath("/publicacoes/4/46")); handleToggleTransparencia(); } },
                                    ]}
                                  />
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/contratos-aquisicoes"));
                                      handleToggleTransparencia();
                                    }}
                                  >
                                    CONTRATOS E AQUISIÇÕES
                                  </SubMenuItem>
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/convenios-repasses"));
                                      handleToggleTransparencia();
                                    }}
                                  >
                                    CONVÊNIOS E REPASSES
                                  </SubMenuItem>
                                  <SubMenuItem
                                    onClick={() => {
                                      navigate(buildPath("/auditoria"));
                                      handleToggleTransparencia();
                                    }}
                                  >
                                    AUDITORIA
                                  </SubMenuItem>
                                </MenuList>
                              </ClickAwayListener>
                            </Paper>
                          </Grow>
                        )}
                      </Popper>
                    </div>
                    {hasMenus && (
                      <div style={{ position: "relative" }}>
                        <MenuListOption
                          ref={anchorOutrasOpcoesRef}
                          aria-controls={openOutrasOpcoes ? "menu-list-outras-opcoes" : undefined}
                          aria-haspopup="true"
                          onClick={handleToggleOutrasOpcoes}
                        >
                          ACESSO A INFORMAÇÕES <ArrowDown />
                        </MenuListOption>
                        <Popper
                          open={openOutrasOpcoes}
                          anchorEl={anchorOutrasOpcoesRef.current}
                          role={undefined}
                          transition
                          disablePortal
                          placement="bottom"
                          modifiers={[
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 8],
                              },
                            },
                            {
                              name: 'preventOverflow',
                              options: {
                                boundary: 'viewport',
                                padding: 8,
                              },
                            },
                          ]}
                          style={{
                            zIndex: 1300,
                            width: '100vw',
                            position: 'fixed',
                            left: '0 !important',
                            right: 0,
                            transform: 'none !important',
                            maxHeight: 'calc(100vh - 200px)',
                            overflowY: 'auto',
                          }}
                        >
                          {({ TransitionProps, placement }) => (
                            <Grow
                              {...TransitionProps}
                              style={{
                                transformOrigin:
                                  placement === "bottom"
                                    ? "center top"
                                    : "center bottom",
                              }}
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  px: 2,
                                  py: 2,
                                  maxHeight: 'calc(100vh - 200px)',
                                  overflowY: 'auto',
                                  backgroundColor: '#f5f5f5',
                                }}
                              >
                                <ClickAwayListener onClickAway={handleCloseOutrasOpcoes}>
                                  <Box
                                    id="menu-list-outras-opcoes"
                                    onKeyDown={handleListKeyDown}
                                    sx={{
                                      width: '100%',
                                      maxWidth: '1536px',
                                    }}
                                  >
                                    <MegaMenuComponent
                                      menus={menus}
                                      onItemClick={() => setOpenOutrasOpcoes(false)}
                                    />
                                  </Box>
                                </ClickAwayListener>
                              </Box>
                            </Grow>
                          )}
                        </Popper>
                      </div>
                    )}
                    <MenuListOption
                      active={checkLocation("/contato")}
                      onClick={() => navigate(buildPath("/contato"))}
                    >
                      FALE CONOSCO
                    </MenuListOption>
                  </MenuListWrapper>
                </Grid>
              </Grid>
            </Toolbar>
          </Container>
        </AppBar>
      </AppBarWrapper>
      <DrawerComponent
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
}
